import type { WebsiteHost } from "@rakazo/contracts";
import { commandVariableName, WEBSITE_CREDENTIALS } from "@rakazo/contracts";

/**
 * The Website teammate's built-in how-to and the block its runs get once the owner has connected
 * the code and the host. The checklist is generic: nothing here names a business or an industry.
 */

export const WEBSITE_UPKEEP_SKILL_NAME = "Website upkeep";

const GITHUB_ENV = commandVariableName(WEBSITE_CREDENTIALS.github);
const HOST_ENV = commandVariableName(WEBSITE_CREDENTIALS.host);
const SSH_ENV = commandVariableName(WEBSITE_CREDENTIALS.ssh);

export const WEBSITE_UPKEEP_SKILL = {
  name: WEBSITE_UPKEEP_SKILL_NAME,
  description: "Check the live site, make one SEO or AI-readability fix, and report.",
  body: `Keep the company website healthy, one focused improvement per run. Use the Website block in your instructions for the site, repository, branch, host and the paths that need a pull request. If that block is missing, say the website is not connected yet and stop.

## Each run
1. Check the live site with the browser: the home page, then the pages it links to most. Note load errors, broken links and missing content.
2. Check the latest deploy on the host. If it failed, fixing it is this run's improvement; change nothing else.
3. Check phone width: resize the browser to 390 pixels wide and look for text that overflows, tap targets under 44 pixels, and sideways scrolling.
4. Pick ONE improvement from the checklist below, the one with the most value for the least risk. Never bundle unrelated changes.
5. Make it in the repository: clone with the GitHub token from the environment, change the files, build locally if the project has a build command, and commit with a message that says what changed and why.
6. Ship it:
   - By default, push a new branch named website-upkeep/<short-topic> and open a pull request. Never push to the default branch.
   - Only if the Website block says direct pushes are allowed, and every changed page is outside its pull-request paths, push to the default branch. The host builds and deploys it.
7. After a push to the default branch, wait for the deploy and check the live page again. If it broke, revert the commit and push the revert.
8. Send the report.

## SEO checklist
- Every page has a unique title under 60 characters and a meta description under 160.
- Every page has a canonical URL, and duplicates point to one page.
- sitemap.xml lists every public page and is linked from robots.txt.
- robots.txt allows the public site and blocks only private paths.
- Structured data (JSON-LD) for the organisation, its products or services, and breadcrumbs.
- One h1 per page; headings in order; images have alt text; links have clear text.

## AI readability (GEO) checklist
- llms.txt at the site root: a short summary of the business and links to its key pages.
- Answer-first writing: each page opens with a plain sentence that answers what it is about.
- Facts in lists and tables: prices, hours, locations, features and specifications.
- FAQ sections with FAQPage structured data for the questions customers ask.
- Pages read well at phone width.

## Rules
- One improvement per run. If nothing on the lists needs doing, change nothing and say so.
- Never invent facts, prices, reviews or claims; use only what the site, memory or the owner already says.
- Never delete pages, change prices or legal text, or touch payment, login or tracking code. Suggest those in the report instead.
- Never print, echo or write the tokens anywhere. Use them only from the environment: $${GITHUB_ENV} for GitHub, $${HOST_ENV} for the host API, $${SSH_ENV} for an SSH key.
- Every change is a commit that can be reverted.

## Report
Send a short report in this shape:
- **Checked:** the live site, the latest deploy and phone width, in one line each.
- **Changed:** the one improvement, with the commit or pull request link.
- **Next:** the next improvement you would make.
- **Needs you:** anything only the owner can decide, or "Nothing".`,
} as const;

/** What the owner connected. Token values are never part of it; only where they live. */
export interface WebsiteTeammateConfig {
  siteUrl: string;
  repo: string;
  branch: string;
  host: WebsiteHost;
  sshTarget: string | null;
  protectedPaths: string[];
  /** The owner lets it push to the default branch; otherwise every change is a pull request. */
  directPush: boolean;
}

const HOST_NAMES: Record<WebsiteHost, string> = {
  vercel: "Vercel",
  netlify: "Netlify",
  cloudflare_pages: "Cloudflare Pages",
  ssh: "your own server over SSH",
};

/** Builds each push to the default branch itself; SSH needs a pull on the server. */
function deployLine(config: WebsiteTeammateConfig): string {
  if (config.host === "ssh") {
    return `Deploy: after a push to ${config.branch}, connect with \`ssh -i\` using the key in $${SSH_ENV} (write it to a 600-mode file outside the repository and delete it afterwards) to ${config.sshTarget ?? "the server"} and pull the branch there.`;
  }
  return `Deploy: ${HOST_NAMES[config.host]} builds each push to ${config.branch}. Read the latest deploy from its API with the token in $${HOST_ENV}.`;
}

/** The block a Website teammate's runs get once its code and host are connected. */
export function websiteTeammateContext(config: WebsiteTeammateConfig): string {
  const paths = config.protectedPaths.length ? config.protectedPaths.join(", ") : "none";
  const shipping = config.directPush
    ? `Direct pushes to ${config.branch} are allowed, except for: ${paths}. A change to any of these opens a pull request instead.`
    : `Open a pull request for every change. Never push to ${config.branch}.`;
  return [
    "## Website",
    `Site: ${config.siteUrl}`,
    `Code: https://github.com/${config.repo} (default branch ${config.branch}). Clone and push over HTTPS with the token in $${GITHUB_ENV}, for example \`https://x-access-token:$${GITHUB_ENV}@github.com/${config.repo}.git\`.`,
    `Host: ${HOST_NAMES[config.host]}`,
    deployLine(config),
    shipping,
    `Follow the "${WEBSITE_UPKEEP_SKILL_NAME}" skill for upkeep runs.`,
  ].join("\n");
}
